import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartBindingValue, CustomChartPoint } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { ChartCardShell } from '../ChartCardShell';
import { chartNumberDigits } from '../../utils';
import { ChartGroupSection } from './ChartGroupSection';
import {
  chartPointShare,
  chartPointValueText,
  chartValueView,
  groupChartItems,
  type ChartAppearance,
  type ChartValueView,
} from '../chartElements';
import { BarChartGroup, type BarChartDatum } from './BarChartGroup';
import { ChartLegend } from './ChartLegend';
import { valueRuleLegendItems } from './chartLegendItems';

type Props = {
  title: string;
  points: CustomChartPoint[];
  /** Пороги зон качества пользователя: по ним окрашивается элемент со своим потолком */
  zoneRanges: QualityZoneRanges;
  /** Оформление диаграммы: направление элементов, цвета категорий и правила раскраски */
  appearance?: ChartAppearance;
  /** Выбор элемента наполняет связанную диаграмму; не задан — элементы не выбираются */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранный элемент — он подсвечен, повторный выбор снимает его */
  selectedElement?: ChartBindingValue;
};

/** Ширина знака подписи величины: по ней отмеряется место под самую длинную величину ряда. */
const VALUE_CHAR_WIDTH = 7;

/**
 * Столбчатая диаграмма: величина по категориям, нарисованная полосой-прогрессом — тем же видом,
 * что показатели стандартного дашборда: незаполненная дорожка во всю отведённую длину, заполненная
 * часть внутри неё, оба конца скруглены, название с одного конца, величина с другого.
 *
 * Оси величины, её делений и сетки у диаграммы нет: величина подписана у самого элемента, и ось
 * говорила бы то же число второй раз, отнимая место у самого рисунка.
 *
 * Концы дорожки заняты каждый своим: с одного стоит название, с другого — величина, и оба стоят за
 * дорожкой, а не на ней. Место под величину отмеряется по самой длинной величине ряда и величина
 * прижата к его дальнему концу — как в строке показателя стандартного дашборда; место под название —
 * по ширине самой диаграммы; тесной карточке уступает название, а дорожки ряда начинаются и
 * заканчиваются на одной черте при любой ширине.
 *
 * Направление элементов выбирает пользователь: полосы удобнее там, где названий много и они
 * длинные, столбцы — там, где названия коротки и важен ход по порядку. Угадать нужное по описанию
 * нельзя — это выбор читателя, а не свойство выборки.
 *
 * Элемент со своим потолком нормируется и окрашивается по нему: доля от собственного потолка —
 * единственная величина, сопоставимая между шкалами разных заключений. Элемент без потолка
 * нормируется наибольшей величиной диаграммы — одной меркой на все группы, чтобы равные величины
 * соседних групп были заполнены одинаково. Вид показанной величины
 * выбирается один раз на диаграмму, чтобы балл одной строки не стоял рядом с процентом соседней.
 *
 * Элементы, отнесённые выборкой к группам, показаны озаглавленными группами в порядке их первого
 * появления: одноимённые элементы разных групп остаются разными.
 */
export const BarChartView: FC<Props> = ({
  title,
  points,
  zoneRanges,
  appearance,
  onSelectElement,
  selectedElement,
}) => {
  const { t } = useTranslation('qualityDashboard');

  // Вид величины и нормировка берутся по всей диаграмме сразу, а не по группе: группа своего
  // потолка не задаёт, и проценты одной группы рядом с баллами соседней читались бы как разное.
  const view = useMemo((): ChartValueView => chartValueView(points), [points]);
  const rowMax = useMemo(() => points.reduce((max, point) => Math.max(max, point.value ?? 0), 0), [points]);
  const digits = useMemo(() => chartNumberDigits(points.map((point) => point.value)), [points]);
  const isVertical = appearance?.barOrientation === 'vertical';

  const groups = useMemo(
    () =>
      groupChartItems(points).map((group) => ({
        title: group.title,
        items: group.items.map((point): BarChartDatum => {
          const share = chartPointShare(point);
          return {
            ...point,
            // Элемент без своего потолка нормируется наибольшей величиной всей диаграммы: она
            // заполняет дорожку до конца, а не до «круглой» отметки выше неё.
            plotted: share ?? (rowMax > 0 && point.value !== null ? point.value / rowMax : 0),
            valueText: chartPointValueText(point, view, digits),
          };
        }),
      })),
    [points, view, rowMax, digits],
  );

  // Место под величину — по самой длинной величине ряда: дорожки всех строк заканчиваются на одной
  // вертикали, а величина остаётся одной строкой при любой длине.
  const valueTextWidth = useMemo(() => {
    const longest = groups.reduce(
      (max, group) => group.items.reduce((groupMax, item) => Math.max(groupMax, item.valueText.length), max),
      0,
    );
    return longest * VALUE_CHAR_WIDTH;
  }, [groups]);

  const legendItems = useMemo(() => valueRuleLegendItems(appearance?.valueColorRules, t), [appearance, t]);

  if (points.length === 0) {
    return (
      <ChartCardShell title={title}>
        <EmptyState />
      </ChartCardShell>
    );
  }

  return (
    <ChartCardShell title={title}>
      <div className="flex flex-col gap-4">
        {groups.map((group) => (
          <ChartGroupSection key={group.title} title={group.title}>
            <BarChartGroup
              items={group.items}
              isVertical={isVertical}
              valueTextWidth={valueTextWidth}
              zoneRanges={zoneRanges}
              appearance={appearance}
              onSelectElement={onSelectElement}
              selectedElement={selectedElement}
            />
          </ChartGroupSection>
        ))}
        <ChartLegend items={legendItems} />
      </div>
    </ChartCardShell>
  );
};
